/**
 * ApplyContainer
 * --
 */
import React, { useCallback } from 'react';
import ApplyPresenter from './ApplyPresenter';
import { useCreateShopInformation } from 'hooks/ShopInformationHooks';
import { useUserAction, useUserStack } from 'store';
import MessageAlert from 'utils';
import { useUpdateAdmin, useCreateAdmin } from 'hooks/AdminHooks';
import { LoadingPage } from 'components';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthApi } from 'api';
import { useCreateShopInformationFile } from 'hooks/ShopInformationFileHooks';
import { FileManager } from 'utils/FileManager';

/* const shopInformationType = {
  shop_information_id?              : number; // 쇼/브/매/지 idx
  store_information?                : { [key: string]: Record<string, any> }; // 매장정보
  mall_information?                 : { [key: string]: Record<string, any> }; // 쇼핑몰 정보
  shop_information_name?            : string; // 쇼/브/매/지 명
  shop_information_content?         : string; // 쇼/브/매/지 설명
  shop_information_address?         : string; // 쇼/브/매/지 주소
  shop_information_address_detail?  : string; // 쇼/브/매/지 상세주소
  application_status?               : string; // 입점신청여부
  correction_status?                : string; // 수정신청여부
  edit_information?                 : { [key: string]: Record<string, any> }; // 수정정보
  latitude                          : number; // 위도
  longitude                         : number; // 경도
  created_at?                       : number; // 생성일
  updated_at?                       : number; // 수정일
  type?                             : (typeof ShopType)[keyof typeof ShopType]; // 타입
  organization_id?                  : number; // 조직 ID
  commission_rate?                  : number; // 수수로율
}; */

/**----------------------------------------
 * [Component] 입점업체 등록: 컨테이너
 * ----------------------------------------
 */
const ApplyContainer = () => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  const location = useLocation();
  const queryString = new URLSearchParams(location.search);
  const mode = queryString.get('mode');

  /* ===== PROPS ===== */
  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();

  /* ===== Mutate ===== */
  const { createShopInformation } = useCreateShopInformation();
  const { updateAdmin } = useUpdateAdmin();
  const { createAdmin } = useCreateAdmin();
  const { createShopInformationFile } = useCreateShopInformationFile();

  /* ===== Ref ===== */
  const nameRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const addressRef = React.useRef(null);
  const addressDetailRef = React.useRef(null);
  const ownerRef = React.useRef(null);
  const storeNumRef = React.useRef(null);
  const businessNumRef = React.useRef(null);
  const adminAccount = React.useRef(null);
  const adminPassword = React.useRef(null);

  /* ===== STATE ===== */
  // 기본 정보
  const [shopInformation, setShopInformation] = React.useState({
    shop_information_name: '', // 쇼핑몰 명
    shop_information_content: '', // 쇼핑몰 설명
    shop_information_postcode: '', // 쇼핑몰 우편번호
    shop_information_address: '', // 쇼핑몰 주소
    shop_information_address_detail: '', // 쇼핑몰 주소 상세
    application_status: 'WAIT', // 입점신청여부
    // correction_status
    edit_information: {},
    type: 'SHOP', // 타입
  });
  // 쇼핑몰 정보 - shop_information > store_information
  const [storeInformation, setStoreInformation] = React.useState({
    store_num: '', // 매장 연락처
    business_num: '', // 사업자등록번호
    store_owner: '', // 대표자
    bank_name: null, // 은행명
    account_number: '', // 입금 계좌번호
  });
  // 관리자 계정정보 -
  const [adminInformation, setAdminInfromation] = React.useState({
    admin_account: '',
    password: '',
    admin_phone: '',
    admin_email: '',
    admin_name: '',
  });
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 사업자 등록증
  const [businessFile, setBusinessFile] = React.useState(null);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**--------------------
   * 데이터 바인딩
   * --------------------
   */
  const onChange = (key1, key2, value) => {
    const cb = (prev) => ({ ...prev, [key2]: value });

    if (key1 === 'shopInformation') {
      setShopInformation(cb);
    } else if (key1 === 'storeInformation') {
      setStoreInformation(cb);
    } else if (key1 === 'adminInformation') {
      setAdminInfromation(cb);
    }
  };
  /**--------------------
   * 쇼핑몰 정보 생성
   * --------------------
   */
  const handleSubmit = async () => {
    // TODO: 필수 입력값 입력 여부 체크
    const {
      shop_information_name,
      shop_information_content,
      shop_information_postcode,
      shop_information_address,
      shop_information_address_detail,
    } = shopInformation;
    const { store_num, business_num, store_owner, bank_name, account_number } =
      storeInformation;
    const { admin_account, admin_phone, admin_email, admin_name, password } =
      adminInformation;

    /* 예외처리 */
    if (!shop_information_name) {
      MessageAlert.warning('브랜드명을 입력해주세요!');
      nameRef.current.focus();
      return;
    }

    if (!shop_information_content) {
      MessageAlert.warning('브랜드명 설명 입력해주세요!');
      contentRef.current.focus();
      return;
    }

    if (!shop_information_postcode || !shop_information_address) {
      MessageAlert.warning('주소를 입력해주세요!');
      addressRef.current.focus();
      return;
    }

    if (!shop_information_address_detail) {
      MessageAlert.warning('상세 주소를 입력해주세요!');
      addressDetailRef.current.focus();
      return;
    }

    if (!shop_information_address_detail) {
      MessageAlert.warning('상세 주소를 입력해주세요!');
      ownerRef.current.focus();
      return;
    }

    if (!store_num) {
      MessageAlert.warning('대표 번호를 입력해주세요.');
      storeNumRef.current.focus();
      return;
    }

    if (!business_num) {
      MessageAlert.warning('사업자 번호를 입력해주세요.');
      businessNumRef.current.focus();
      return;
    }

    if (!store_owner) {
      MessageAlert.warning('대표자명을 입력해주세요.');
      ownerRef.current.focus();
      return;
    }

    if (!bank_name) {
      MessageAlert.warning('입금받으실 은행을 입력해주세요.');
      return;
    }

    if (!account_number) {
      MessageAlert.warning('입금받으실 계좌번호를 입력해주세요.');
      return;
    }

    if (!businessFile) {
      MessageAlert.warning('사업자 등록증 파일을 업로드해주세요.');
      return;
    }

    if (
      !admin_account ||
      !admin_email ||
      !admin_phone ||
      !admin_name ||
      !password
    ) {
      MessageAlert.warning('관리자 계정을 확인해주세요.');
      return;
    }

    // 관리자 계정 중복검사
    const duplicateCheck = await AuthApi.duplicateCheck({
      admin_account: admin_account,
      type: 'ADMIN',
    });

    if (duplicateCheck?.status !== 200 || !duplicateCheck?.data) {
      MessageAlert.warning('사용할 수 없는 아이디입니다.');
      return;
    }

    try {
      // NOTE: 로딩 시작
      setIsLoading(true);

      // REVIEW: 쇼핑몰 생성 프로세스 [쇼핑몰 생성 -> 관리자 업데이트 -> 스토어 업데이트]
      // NOTE: 쇼핑몰 정보 생성
      const newData = {
        ...shopInformation,
        store_information: storeInformation,
        mall_information: storeInformation,
      };

      const { status: shopStatus, data: shopData } =
        await createShopInformation(newData);

      // NOTE: 쇼핑몰 생성 성공 처리
      if (shopStatus === 200) {
        const { shop_information_id, created_at, updated_at, type, ...d } =
          shopData;

        // NOTE: 사업자 등록증 업로드
        const { status: uploadStatus, data: uploadData } =
          await FileManager.upload(`${shop_information_id}`, businessFile);

        // NOTE: 파일 업로드 성공 시 매장 파일 데이터 생성
        if (uploadStatus === 200) {
          const fileData = {
            shop_information_id: shop_information_id,
            file_name: uploadData?.origin_name,
            file_url: uploadData?.file_name,
            type: 'LICENSE',
          };
          const { status: fileStatus } = await createShopInformationFile(
            fileData
          );

          if (fileStatus !== 200) {
            MessageAlert.error('사업자 등록증 업로드에 실패하였습니다.');
          }
        }

        // NOTE: 관리자 업데이트
        const { status: adminStatus } = await createAdmin({
          admin_account,
          admin_phone,
          admin_email,
          admin_name,
          password,
          type: 'SHOP_ADMIN',
          shop_information_id: shop_information_id,
        });

        // NOTE: 관리자 업데이트 성공 처리
        if (adminStatus === 200) {
          // NOTE: 스토어 업데이트
          const newUser = {
            ...userInfo,
            ...d,
            shop_information_id,
          };

          userAction.setUser(newUser);
          if (mode === 'admin' || mode === 'popup') {
            navigate('/brand/list', { replace: 'true' });
          } else {
            navigate(`/main`, {
              replace: 'true',
            });
          }
        }
        // TODO: 관리자 업데이트 실패 처리
        else {
          MessageAlert.error(
            `쇼핑몰 정보를 업데이트하는데 에러가 발생하였습니다. 관리자에게 문의해주세요.`
          );
        }
      }
      // TODO: 쇼핑몰 생성 실패 처리
      else {
        MessageAlert.error(
          `쇼핑몰 정보를 등록하는데 에러가 발생하였습니다. 관리자에게 문의해주세요.`
        );
      }
    } catch (err) {
      console.error(`[CREATE SHOP INFORMATION] ERROR: `, err);
      MessageAlert.error(`쇼핑몰 정보를 등록하는데 에러가 발생하였습니다`);
      throw err;
    } finally {
      // NOTE: 로딩 종료
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**--------------------
   * 데이터 바인딩 함수
   * --------------------
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = useCallback((t, v, o, callback = null) => {
    callback((prev) => {
      if (prev[t] === v) return prev;
      return { ...prev, [t]: v };
    });
  }, []);

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && <LoadingPage text={'쇼핑몰 정보를 등록하고 있습니다.'} />}
      <ApplyPresenter
        {...{
          /* states */
          shopInformation,
          storeInformation,
          adminInformation,
          businessFile,
          /* refs */
          nameRef,
          contentRef,
          storeNumRef,
          businessNumRef,
          adminAccount,
          adminPassword,
          addressRef,
          addressDetailRef,
          ownerRef,
          /* events */
          setShopInformation,
          setStoreInformation,
          onSubmit: handleSubmit,
          handleChange,
          onChange,
          setBusinessFile,
        }}
      />
    </>
  );
};

export default ApplyContainer;
